import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useCartStore } from '@/stores/cart-store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Turnstile } from '@/components/Turnstile';
import { Honeypot } from '@/components/Honeypot';
import { IS_RUMIO, IS_SAAS } from '@/config/site';
import { useSettings } from '@/hooks/useProducts';
import { resolvePostAuthDest } from '@/lib/post-auth-dest';
import { isSafeReturnUrl } from '@/lib/safe-return-url';
import { isVerticalId } from '@/pages/public/saas/verticals';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import type { SocialProviderId } from '@/lib/auth';

export default function Signup() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // See Login.tsx — same DB-first, env-fallback resolution.
  const { data: settingsData } = useSettings();
  const turnstileSiteKey =
    (settingsData?.settings?.turnstile_site_key as string | undefined) ||
    import.meta.env.VITE_TURNSTILE_SITE_KEY ||
    '';
  const planParam = searchParams.get('plan');
  const refParam = searchParams.get('ref');
  // Rumio platform customers land on the onboarding questionnaire, preserving
  // any ?plan=… selection from the pricing page so the wizard provisions the
  // right tier. Tenant users resolve their destination at submit time from the
  // freshly-created user via resolvePostAuthDest — e.g. a new SaaS customer
  // (no subscription yet) is sent to /pricing to convert.
  // `?type=` is the site type picked on the marketing site (/lodging, …). It
  // rides along so the Site Builder opens on that type instead of its default.
  // Only the known ids are forwarded.
  const typeParam = searchParams.get('type');
  const onboardingQuery = new URLSearchParams();
  if (planParam) onboardingQuery.set('plan', planParam);
  if (isVerticalId(typeParam)) onboardingQuery.set('type', typeParam);
  const onboardingQs = onboardingQuery.toString();
  const onboardingDest = `/onboarding${onboardingQs ? `?${onboardingQs}` : ''}`;
  // `?redirect=` — where a tenant visitor was going before they had to make an
  // account: typically an app's invite link (/invite/:code), handed on by
  // Login's "Create account". Checked exactly as Login checks it. Rumio's own
  // signup ignores it: the platform's destination is always onboarding.
  const safeRedirect = IS_RUMIO ? null : isSafeReturnUrl(searchParams.get('redirect'));

  const register = useAuthStore((s) => s.register);
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const loginWithProvider = useAuthStore((s) => s.loginWithProvider);
  const isLoading = useAuthStore((s) => s.isLoading);
  const mergeGuestCart = useCartStore((s) => s.mergeGuestCart);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  // An already-authenticated visitor who lands on the signup page has no
  // reason to see a registration form — and submitting it fails, because the
  // account already exists, which drops them on /login. Where to send them
  // instead depends on the kind of site, and there are THREE kinds, not two:
  //
  //   Rumio itself → the onboarding/site-builder flow (an existing customer
  //                  clicking "Start free trial" is adding another site),
  //                  preserving any ?plan= selection.
  //   Another SaaS → wherever resolvePostAuthDest() points: checkout at
  //                  /account/billing?plan=… when they picked a tier on
  //                  /pricing, or /pricing itself when they haven't yet.
  //   A shop       → keep the form. A shopper may legitimately be creating a
  //                  second account, converting a guest session, and so on.
  //
  // Lumping SaaS tenants in with shops is what closed the loop: /pricing's CTA
  // points at /signup?plan=…, so a signed-in customer bounced between the two
  // pages and could never reach checkout.
  useEffect(() => {
    if (!isInitialized || !user) return;
    if (IS_RUMIO) {
      navigate(onboardingDest, { replace: true });
    } else if (safeRedirect) {
      // Signed in already, and on the way somewhere: go there. Applies to
      // shops too — the form is kept for a shopper with nowhere to be, not
      // for one following a link.
      goTo(safeRedirect);
    } else if (IS_SAAS) {
      navigate(resolvePostAuthDest(user, planParam), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, user, navigate, onboardingDest, planParam, safeRedirect]);

  // A safe redirect can be an absolute same-host URL; react-router only
  // navigates paths. Same split as Login's navigateAfterLogin.
  function goTo(destination: string) {
    if (/^https?:\/\//i.test(destination)) {
      window.location.assign(destination);
      return;
    }
    navigate(destination, { replace: true });
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('signup.errors.nameRequired');
    }

    if (!email.trim()) {
      newErrors.email = t('signup.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('signup.errors.invalidEmail');
    }

    if (!password) {
      newErrors.password = t('signup.errors.passwordRequired');
    } else if (password.length < 8) {
      newErrors.password = t('signup.errors.tooShort');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('signup.errors.confirmRequired');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('signup.errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setGeneralError('');

    if (!validate()) return;

    try {
      await register(email, password, name, turnstileToken, refParam ?? undefined);
      if (!IS_RUMIO) await mergeGuestCart();
      const dest = IS_RUMIO
        ? onboardingDest
        : (safeRedirect ?? resolvePostAuthDest(useAuthStore.getState().user, planParam));
      goTo(dest);
    } catch (err: any) {
      setGeneralError(err?.message || t('signup.errors.generic'));
    }
  }

  // OAuth round-trips can't carry the in-page state we have here, so stash it
  // in sessionStorage for the /auth/callback page to consume once the session
  // lands:
  //   - the referral code → POSTed to /api/referrals/attribute.
  //   - the post-auth destination → on Rumio the email/password path navigates
  //     to onboardingDest on submit, but the OAuth path hands off to the IdP and
  //     loses that. Without this stash, social signup falls through to
  //     resolvePostAuthDest() which sends Rumio users to /admin — so a brand-new
  //     trial signup (or an existing customer adding a site) never reaches the
  //     wizard. AuthCallback re-validates the value via isSafeReturnUrl.
  function persistOAuthIntent() {
    if (refParam) {
      try {
        sessionStorage.setItem('pending_ref_code', refParam);
      } catch {
        /* private mode / disabled storage — silent no-op */
      }
    }
    if (IS_RUMIO) {
      try {
        sessionStorage.setItem('pending_login_redirect', onboardingDest);
      } catch {
        /* private mode / disabled storage — silent no-op */
      }
    } else if (safeRedirect) {
      // Where they were going wins over where a plan would send them: it is
      // the more specific intent, and AuthCallback re-validates it.
      try {
        sessionStorage.setItem('pending_login_redirect', safeRedirect);
      } catch {
        /* private mode / disabled storage — silent no-op */
      }
    } else if (IS_SAAS && planParam) {
      // Same problem on a SaaS tenant: the tier the visitor picked on
      // /pricing is in the URL, and an OAuth hand-off drops it. Without this,
      // social signup lands on /pricing again instead of checkout.
      try {
        sessionStorage.setItem(
          'pending_login_redirect',
          `/account/billing?plan=${encodeURIComponent(planParam)}`,
        );
      } catch {
        /* private mode / disabled storage — silent no-op */
      }
    }
  }

  async function handleSocialSignup(provider: SocialProviderId) {
    try {
      persistOAuthIntent();
      await loginWithProvider(provider);
    } catch (err: any) {
      setGeneralError(err?.message || t(`signup.errors.${provider}Failed`));
    }
  }

  return (
    <main className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center">
          {/* Same three-way split as the redirect above. The Workspace copy
              sells Rumio ("your AI-powered store"), the Shop copy is written
              for a storefront — neither describes a SaaS tenant selling its
              own subscription product, which is how a meal planner ended up
              inviting people to "start growing something beautiful". Tenants
              that want their own wording override these keys via the text
              overrides admin; these are the defaults. */}
          <h1 className="text-3xl font-bold text-ink">
            {IS_RUMIO
              ? t('signup.headingWorkspace')
              : IS_SAAS
                ? t('signup.headingSubscribe')
                : t('signup.headingShop')}
          </h1>
          <p className="mt-2 text-ink-soft">
            {IS_RUMIO
              ? t('signup.subtitleWorkspace')
              : IS_SAAS
                ? t('signup.subtitleSubscribe')
                : t('signup.subtitleShop')}
          </p>
        </div>

        <div className="mt-8 rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-8">
          {/* General error alert */}
          {generalError && (
            <div
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              {generalError}
            </div>
          )}

          {/* Social sign-up — see the matching block in Login.tsx. */}
          <SocialLoginButtons
            namespace="signup"
            disabled={isLoading}
            onProvider={handleSocialSignup}
            divider={
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-surface px-4 text-ink-muted">{t('signup.orWithEmail')}</span>
                </div>
              </div>
            }
          />

          {/* Registration form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4 relative">
            <Honeypot />
            <Input
              label={t('signup.nameLabel')}
              type="text"
              autoComplete="name"
              required
              placeholder={t('signup.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
            />

            <Input
              label={t('signup.emailLabel')}
              type="email"
              autoComplete="email"
              required
              placeholder={t('signup.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
            />

            <Input
              label={t('signup.passwordLabel')}
              type="password"
              autoComplete="new-password"
              required
              placeholder={t('signup.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              helperText={!errors.password ? t('signup.passwordHelp') : undefined}
            />

            <Input
              label={t('signup.confirmPasswordLabel')}
              type="password"
              autoComplete="new-password"
              required
              placeholder={t('signup.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
            />

            <Turnstile
              siteKey={turnstileSiteKey}
              onVerify={setTurnstileToken}
            />

            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!!turnstileSiteKey && !turnstileToken}
              className="w-full"
              size="lg"
            >
              {t('signup.submit')}
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-ink-muted">
            {t('signup.agreePrefix')}{' '}
            <Link
              to="/page/terms-of-service"
              className="text-sage-700 hover:text-sage-800 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('signup.termsLink')}
            </Link>{' '}
            {t('signup.andConnector')}{' '}
            <Link
              to="/page/privacy-policy"
              className="text-sage-700 hover:text-sage-800 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('signup.privacyLink')}
            </Link>
            .
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-ink-soft">
          {t('signup.haveAccount')}{' '}
          <Link
            to={safeRedirect ? `/login?redirect=${encodeURIComponent(safeRedirect)}` : '/login'}
            className="font-medium text-sage-700 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
          >
            {t('signup.signIn')}
          </Link>
        </p>
      </div>
    </main>
  );
}
